import {
	APP_LAUNCH_FALLBACK_MS,
	EXPECTED_LEAVE_FALLBACK_MS,
	HANDOFF_FLIP_MAX_MS,
	LEAVING_FALLBACK_MS,
	RETURN_SETTLE_MS,
} from '#src/app/pages/psf/share/shared/consts';

type Listener = (isPresent: boolean) => void;

type Options = Readonly<{
	/**
	 * Wait `RETURN_SETTLE_MS` past the first frame after coming back from another app or tab: Android reports
	 * the page visible as its return animation starts, while a snapshot still covers it.
	 */
	settleOnReturn?: boolean;
}>;

const listeners = new Set<Listener>();
let isPresent = true;
let isLeaving = false;
let isExpectingLeave = false;
let isLaunchingApp = false;
let isSettling = false;
let settleOnReturn = false;
let hiddenAt: number | undefined;
let blurTimer: ReturnType<typeof setTimeout> | undefined;
let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
let settleTimer: ReturnType<typeof setTimeout> | undefined;
let settleFrame: number | undefined;
let stopListening: (() => void) | undefined;

function update() {
	const next = document.visibilityState === 'visible' && !isLeaving && !isSettling;
	if (next === isPresent) return;
	isPresent = next;
	listeners.forEach((listener) => listener(next));
}

function stopLeaving() {
	clearTimeout(blurTimer);
	clearTimeout(fallbackTimer);
	isLeaving = false;
	isExpectingLeave = false;
	isLaunchingApp = false;
	update();
}

function startLeaving(fallbackMs: number) {
	clearTimeout(fallbackTimer);
	isLeaving = true;
	fallbackTimer = setTimeout(stopLeaving, fallbackMs);
	update();
}

function stopSettling() {
	if (settleFrame !== undefined) cancelAnimationFrame(settleFrame);
	clearTimeout(settleTimer);
	settleFrame = undefined;
	isSettling = false;
}

function endSettling() {
	stopSettling();
	update();
}

function startSettling() {
	stopSettling();
	if (!settleOnReturn) return;
	isSettling = true;
	// Capped in case no frame comes while the page is visible.
	settleTimer = setTimeout(endSettling, RETURN_SETTLE_MS * 2);
	settleFrame = requestAnimationFrame(() => {
		clearTimeout(settleTimer);
		settleTimer = setTimeout(endSettling, RETURN_SETTLE_MS);
	});
}

function onBlur() {
	// Already waiting for the supporter to leave, with its own fallback.
	if (isLeaving) return;
	clearTimeout(blurTimer);
	// Checked a tick later, once focus has landed: moving into an iframe on the page isn't leaving.
	blurTimer = setTimeout(() => {
		if (document.hasFocus() || document.activeElement instanceof HTMLIFrameElement) return;
		startLeaving(LEAVING_FALLBACK_MS);
	});
}

function onFocus() {
	// Chrome on Android gives focus back while it launches the app (see `onVisibilityChange`).
	if (isLaunchingApp) return;
	stopLeaving();
}

function onPointerDown() {
	stopSettling();
	stopLeaving();
}

function onVisibilityChange() {
	if (document.visibilityState !== 'visible') {
		hiddenAt = performance.now();
		stopSettling();
		if (isExpectingLeave) update();
		else stopLeaving();
		return;
	}

	const awayMs = hiddenAt === undefined ? 0 : performance.now() - hiddenAt;
	const wasHidden = hiddenAt !== undefined;
	hiddenAt = undefined;
	// Chrome on Android opens an app link's new tab, then closes it as soon as it has asked for the app,
	// showing this page again for the second or so the app takes to start. That isn't the supporter coming back.
	if (isExpectingLeave && awayMs < HANDOFF_FLIP_MAX_MS) {
		isLaunchingApp = true;
		startLeaving(APP_LAUNCH_FALLBACK_MS);
		return;
	}
	if (wasHidden) startSettling();
	stopLeaving();
}

function startListening(): () => void {
	isLeaving = false;
	isExpectingLeave = false;
	isLaunchingApp = false;
	isSettling = false;
	hiddenAt = undefined;
	isPresent = document.visibilityState === 'visible';
	window.addEventListener('blur', onBlur);
	window.addEventListener('focus', onFocus);
	window.addEventListener('pageshow', stopLeaving);
	document.addEventListener('pointerdown', onPointerDown);
	document.addEventListener('visibilitychange', onVisibilityChange);
	return () => {
		clearTimeout(blurTimer);
		clearTimeout(fallbackTimer);
		stopSettling();
		settleOnReturn = false;
		window.removeEventListener('blur', onBlur);
		window.removeEventListener('focus', onFocus);
		window.removeEventListener('pageshow', stopLeaving);
		document.removeEventListener('pointerdown', onPointerDown);
		document.removeEventListener('visibilitychange', onVisibilityChange);
	};
}

/**
 * Calls `onChange` with whether the supporter is on the page, straight away and then whenever it changes.
 *
 * Losing focus counts as leaving too: Chrome on iOS only reports a page hidden once its new-tab animation
 * ends, ~0.9s after a share opens a tab, while `blur` arrives straight away. Focus coming back, the page
 * becoming visible, a tap or `LEAVING_FALLBACK_MS` end that.
 *
 * The state is shared by every subscriber, so one that subscribes after the supporter left (the post-share
 * redirect, once the share's requests resolve) starts from what the counter, subscribed all along, has seen.
 */
export function subscribeToSupporterPresence(onChange: Listener, options: Options = {}): () => void {
	if (!listeners.size) stopListening = startListening();
	if (options.settleOnReturn !== undefined) settleOnReturn = options.settleOnReturn;
	listeners.add(onChange);
	onChange(isPresent);

	return () => {
		listeners.delete(onChange);
		if (listeners.size) return;
		stopListening?.();
		stopListening = undefined;
	};
}

/**
 * Tells subscribers the supporter is about to leave for another app, before the page can know it: iOS and
 * Android only report it hidden once the switch has finished, ~1s after the tap. They count as away until
 * they're back, or `EXPECTED_LEAVE_FALLBACK_MS` if the page never hides; if Chrome on Android shows the page
 * again while it launches the app, focus doesn't count and the wait is `APP_LAUNCH_FALLBACK_MS`.
 */
export function expectSupporterToLeave(): void {
	if (!listeners.size) return;
	clearTimeout(blurTimer);
	isExpectingLeave = true;
	isLaunchingApp = false;
	startLeaving(EXPECTED_LEAVE_FALLBACK_MS);
}
